import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const WHY_TIKTOK = [
  {
    title: "Massive organic reach",
    description:
      "TikTok's algorithm shows your content to people who have never heard of you. A single property tour can reach tens of thousands of potential buyers and vendors without spending a penny on ads.",
  },
  {
    title: "Younger audience, future clients",
    description:
      "First-time buyers aged 25-35 are the most active demographic on TikTok. These are tomorrow's vendors. Building your brand with them now pays dividends for years.",
  },
  {
    title: "Local discovery",
    description:
      "TikTok's location-based recommendations surface local content to local users. A property video in Bath will naturally reach people searching for homes in Bath.",
  },
  {
    title: "Vendor impression",
    description:
      "When a potential vendor Googles your agency, they will check your social media. An active TikTok with property content signals a modern, marketing-savvy agent.",
  },
]

const CONTENT_TYPES = [
  {
    title: "Property reveals",
    description:
      "The classic TikTok property format: open the front door and walk through the home. Use a transition from the exterior to the interior for maximum impact. Keep it under 30 seconds.",
  },
  {
    title: "Before and after staging",
    description:
      "Show a room before and after staging or renovation. The transformation format is hugely popular on TikTok and gets strong engagement from both buyers and homeowners.",
  },
  {
    title: "Day in the life",
    description:
      "Take viewers through a typical day as an estate agent. Valuations, viewings, negotiations, keys being handed over. Authentic content builds trust.",
  },
  {
    title: "Price guessing games",
    description:
      "'How much is this house?' Show the exterior, walk through the rooms, then reveal the price at the end. Highly engaging because viewers watch to the end, which the algorithm loves.",
  },
  {
    title: "Local area spotlights",
    description:
      "Tour a neighbourhood: the best coffee shop, the park, the high street, the school run route. This positions you as the local expert and attracts both buyers and vendors.",
  },
  {
    title: "Myth-busting and tips",
    description:
      "'Three things your estate agent won't tell you' or 'What to look for at a viewing'. Educational content gets saved, shared, and builds authority.",
  },
]

const TIKTOK_TIPS = [
  { tip: "Hook in the first second", detail: "Start with movement or a bold statement. Never open with a static image or logo." },
  { tip: "Film vertically (9:16)", detail: "TikTok is portrait-first. Always film in vertical orientation." },
  { tip: "Use native captions", detail: "Add TikTok's auto-captions. Most viewers watch without sound." },
  { tip: "Post 3-5 times per week", detail: "Consistency is more important than perfection on TikTok." },
  { tip: "Engage with comments", detail: "Reply to comments with video replies. This creates new content and boosts reach." },
  { tip: "Use location tags", detail: "Always add a location tag. It helps TikTok show your content to local users." },
  { tip: "Keep videos under 60 seconds", detail: "Shorter videos get shown to more people. Save long-form for YouTube." },
  { tip: "Follow property trends", detail: "Watch what other property creators do well and adapt it for your market." },
]

const STATS = [
  { stat: "1B+", label: "monthly active TikTok users worldwide" },
  { stat: "52min", label: "average daily time spent on TikTok" },
  { stat: "67%", label: "of users discover new brands on TikTok" },
  { stat: "30s", label: "ideal property video length for TikTok" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Is TikTok worth it for estate agents?",
    a: "Yes. TikTok offers the best organic reach of any social platform right now. A single property video can reach thousands of potential buyers and vendors without any advertising spend. Even if your current clients are not on TikTok, tomorrow's clients are. Agents who build a presence now will have a significant advantage over those who wait.",
  },
  {
    q: "What kind of properties work best on TikTok?",
    a: "All properties can work. Aspirational homes with striking features get the most views, but everyday family homes perform well too because they are relatable. The key is the presentation, not the price tag. A well-filmed two-bed terrace will outperform a poorly filmed mansion every time.",
  },
  {
    q: "How do I get started on TikTok as an estate agent?",
    a: "Create a TikTok Business account, set up your profile with your agency name, location, and contact details, then start posting. Your first 10 videos will feel awkward. That is normal. Focus on property tours and local area content. Use your phone; you do not need professional equipment. The algorithm favours new accounts, so your early posts often get extra reach.",
  },
  {
    q: "Do I need to do trending dances and memes?",
    a: "No. Property content works brilliantly on TikTok without following every trend. Use trending sounds when they fit naturally, but your core content should be property tours, area guides, market updates, and behind-the-scenes footage. Authenticity matters more than trend-chasing.",
  },
  {
    q: "Can I repurpose TikTok content on other platforms?",
    a: "Yes. The same video works on Instagram Reels, YouTube Shorts, and Facebook Reels. Film once, post everywhere. Remove the TikTok watermark before posting on Instagram (Instagram deprioritises watermarked content). QuiteRight videos are rendered without watermarks for this reason.",
  },
  {
    q: "How long before I see results from TikTok?",
    a: "Expect 2-3 months of consistent posting before you see meaningful traction. Some videos will get 200 views; others might reach 50,000. That unpredictability is part of the platform. Focus on consistency (3-5 posts per week) and quality, and the results will follow.",
  },
  {
    q: "Can I use QuiteRight videos on TikTok?",
    a: "Yes. QuiteRight renders videos in portrait format (9:16) that is perfect for TikTok. Download the portrait version and post it directly. The branded video with voiceover and music is ready to publish without any additional editing.",
  },
  {
    q: "Should I use TikTok ads as an estate agent?",
    a: "Start with organic content first. Once you have a library of content and understand what resonates with your audience, TikTok ads can amplify your best-performing videos. The minimum daily budget is around 20 pounds, and you can target by location, age, and interests. But organic reach on TikTok is so strong that many agents never need to pay for ads.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <div className="divide-y divide-stone">
      {items.map((item) => (
        <details key={item.q} className="group py-5">
          <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
            {item.q}
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
            >
              <path
                fillRule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
        </details>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function TikTokEstateAgentsGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "TikTok for Estate Agents: How to Win Listings with Short-Form Video",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "TikTok for Estate Agents" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            TikTok for estate agents
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How to use TikTok to win instructions, reach first-time buyers, and
            build your agency brand. Content ideas, practical tips, and a
            strategy that does not require you to dance.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Why TikTok */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="text-center font-display text-2xl font-bold text-charcoal md:text-3xl">
              Why TikTok matters for estate agents
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-10 grid gap-6 sm:grid-cols-2">
            {WHY_TIKTOK.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {item.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Content types */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              What to post
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Six TikTok content ideas for agents
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CONTENT_TYPES.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {item.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Tips checklist */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Best practices
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Eight tips for better TikToks
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-3">
            {TIKTOK_TIPS.map((item, i) => (
              <ScrollReveal key={i}>
                <div className="flex items-start gap-4 rounded-xl border border-stone bg-white p-5">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red font-display text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-display text-sm font-bold text-charcoal">
                      {item.tip}
                    </p>
                    <p className="mt-1 text-sm text-slate">{item.detail}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/guides/instagram-reels-estate-agents", title: "Instagram Reels for estate agents", desc: "The complete guide to Instagram Reels for property marketing." },
              { href: "/guides/social-media-estate-agents", title: "Social media for estate agents", desc: "Platform-by-platform strategies for all major social networks." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            TikTok-ready property videos
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight creates portrait-format property videos with voiceover
            and music, ready to post on TikTok. Paste a listing URL, pick a
            template, and download in 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
